import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { newKey } from "../../lib/format";
import { INP, LBL } from "./shared";

const RATE_FIELDS = [
  ["barrel_55", "55 Gal"], ["barrel_77", "77 Gal"], ["box_cuft", "Box /cu ft"], ["pallet_cuft", "Pallet /cu ft"], ["min_charge", "Min. item"],
];
const ZERO_RATE = Object.fromEntries(RATE_FIELDS.map(([k]) => [k, 0]));

function Num({ value, onChange, testId, className = "" }) {
  return <input type="number" min="0" step="0.01" defaultValue={value} onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} className={`${INP} ${className}`} data-testid={testId} />;
}

function Card({ title, hint, children, action }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5">
      <div className="flex justify-between items-start mb-4">
        <div><h2 className="font-display font-bold text-[#0F172A]">{title}</h2>{hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function AddBtn({ onClick, label, testId }) {
  return <button type="button" onClick={onClick} className="text-xs font-semibold text-[#1E2A78] inline-flex items-center gap-1 hover:underline" data-testid={testId}><Plus className="h-3.5 w-3.5" /> {label}</button>;
}

function OriginsCard({ p, setP }) {
  const upd = (id, k, v) => setP({ ...p, origins: p.origins.map((o) => (o.id === id ? { ...o, [k]: v } : o)) });
  const add = () => {
    const id = `o_${newKey()}`;
    setP({ ...p, origins: [...p.origins, { id, name: "New area", country: "Canada", currency: "CAD", pickup_fee: 0, auto_confirm: false }],
      rates: { ...p.rates, [id]: Object.fromEntries(p.destinations.map((d) => [d.id, { ...ZERO_RATE }])) } });
  };
  const remove = (id) => { const { [id]: _, ...rates } = p.rates; setP({ ...p, origins: p.origins.filter((o) => o.id !== id), rates }); };
  return (
    <Card title="Shipping-from areas" hint="'Instant booking' areas don't need a confirmation call. All other areas are flagged 'Call to confirm'." action={<AddBtn onClick={add} label="Add area" testId="pricing-add-origin" />}>
      <div className="space-y-2">
        {p.origins.map((o) => (
          <div key={o.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`pricing-origin-${o.id}`}>
            <input className={`${INP} col-span-4`} value={o.name} onChange={(e) => upd(o.id, "name", e.target.value)} data-testid={`pricing-origin-name-${o.id}`} />
            <select className={`${INP} col-span-2`} value={o.country} onChange={(e) => upd(o.id, "country", e.target.value)}><option>Canada</option><option>USA</option></select>
            <select className={`${INP} col-span-2`} value={o.currency} onChange={(e) => upd(o.id, "currency", e.target.value)}><option>CAD</option><option>USD</option></select>
            <div className="col-span-2"><Num value={o.pickup_fee} onChange={(v) => upd(o.id, "pickup_fee", v)} testId={`pricing-origin-pickup-${o.id}`} /></div>
            <label className="col-span-1 text-[10px] flex items-center gap-1"><input type="checkbox" checked={o.auto_confirm} onChange={(e) => upd(o.id, "auto_confirm", e.target.checked)} className="accent-[#1E2A78]" data-testid={`pricing-origin-auto-${o.id}`} />Instant</label>
            <button type="button" onClick={() => remove(o.id)} disabled={p.origins.length === 1} className="col-span-1 text-slate-400 hover:text-red-600 justify-self-center disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-400"><span className="col-span-4">Area name</span><span className="col-span-2">Country</span><span className="col-span-2">Currency</span><span className="col-span-2">Pickup fee</span><span className="col-span-2">Instant booking</span></div>
      </div>
    </Card>
  );
}

function DestinationsCard({ p, setP }) {
  const add = () => {
    const id = `d_${newKey()}`;
    setP({ ...p, destinations: [...p.destinations, { id, name: "New destination" }],
      rates: Object.fromEntries(Object.entries(p.rates).map(([o, r]) => [o, { ...r, [id]: { ...ZERO_RATE } }])) });
  };
  const remove = (id) => setP({ ...p, destinations: p.destinations.filter((d) => d.id !== id),
    rates: Object.fromEntries(Object.entries(p.rates).map(([o, r]) => { const { [id]: _, ...rest } = r; return [o, rest]; })) });
  return (
    <Card title="Destinations" action={<AddBtn onClick={add} label="Add destination" testId="pricing-add-destination" />}>
      <div className="grid sm:grid-cols-2 gap-2">
        {p.destinations.map((d) => (
          <div key={d.id} className="flex gap-2">
            <input className={INP} value={d.name} onChange={(e) => setP({ ...p, destinations: p.destinations.map((x) => (x.id === d.id ? { ...x, name: e.target.value } : x)) })} data-testid={`pricing-dest-name-${d.id}`} />
            <button type="button" onClick={() => remove(d.id)} disabled={p.destinations.length === 1} className="text-slate-400 hover:text-red-600 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RatesCard({ p, setP }) {
  const [oid, setOid] = useState(p.origins[0]?.id);
  const origin = p.origins.find((o) => o.id === oid) || p.origins[0];
  const upd = (did, k, v) => setP({ ...p, rates: { ...p.rates, [origin.id]: { ...p.rates[origin.id], [did]: { ...(p.rates[origin.id]?.[did] || ZERO_RATE), [k]: v } } } });
  return (
    <Card title="Rates by route" hint={`Prices in ${origin?.currency}. Boxes & pallets = cubic feet × rate (never below the minimum).`}
      action={<select className={`${INP} w-64`} value={origin?.id} onChange={(e) => setOid(e.target.value)} data-testid="pricing-rates-origin">{p.origins.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="text-left py-2 pr-2">Destination</th>{RATE_FIELDS.map(([k, l]) => <th key={k} className="text-left py-2 px-1">{l}</th>)}</tr></thead>
          <tbody>
            {p.destinations.map((d) => (
              <tr key={d.id} className="border-t border-slate-100">
                <td className="py-1.5 pr-2 whitespace-nowrap">{d.name}</td>
                {RATE_FIELDS.map(([k]) => (
                  <td key={k} className="py-1.5 px-1 min-w-[90px]">
                    <Num key={`${origin.id}-${d.id}-${k}`} value={p.rates[origin.id]?.[d.id]?.[k] ?? 0} onChange={(v) => upd(d.id, k, v)} testId={`rate-${origin.id}-${d.id}-${k}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function PricingTab() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/pricing").then((r) => setP(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);

  const save = async () => {
    setSaving(true);
    try { setP((await api.put("/pricing", p)).data); toast.success("Prices saved — the quote page now uses them"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!p) return <div className="text-slate-500">Loading prices...</div>;
  return (
    <div className="space-y-5" data-testid="pricing-tab">
      <div className="flex flex-wrap gap-4 items-end justify-between bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex gap-4">
          <div><label className={LBL}>USD per 1 CAD</label><Num value={p.exchange_usd_per_cad} onChange={(v) => setP({ ...p, exchange_usd_per_cad: v })} testId="pricing-fx" className="w-32" /></div>
          <div><label className={LBL}>Pallet lift-gate fee</label><Num value={p.liftgate_fee} onChange={(v) => setP({ ...p, liftgate_fee: v })} testId="pricing-liftgate" className="w-32" /></div>
        </div>
        <button onClick={save} disabled={saving} className="px-5 py-2.5 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] inline-flex items-center gap-2 disabled:opacity-60" data-testid="pricing-save-btn">
          <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save all prices"}
        </button>
      </div>
      <RatesCard p={p} setP={setP} />
      <OriginsCard p={p} setP={setP} />
      <DestinationsCard p={p} setP={setP} />
    </div>
  );
}
